import { useEffect, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";

import {
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { type SavedSearchOperator } from "@north-beam/nb-client-side";
import { SecondaryButton, TertiaryButton } from "@shared/buttons";
import { Select } from "@shared/selects";
import { TextAreaInput } from "@shared/textarea-input";

import { savedSearchOperatorOptions } from "../utils";

interface Props {
  isOpen: boolean;
  defaultQuery: string;
  defaultOperator: SavedSearchOperator;
  onOpenChange(value: boolean): void;
  onApply(operator: SavedSearchOperator, query: string): void;
  onCancel(): void;
}

export const SearchMethodCommaPopover = ({
  isOpen,
  defaultQuery,
  defaultOperator,
  onOpenChange,
  onApply,
  onCancel,
}: Props) => {
  const [query, setQuery] = useState(defaultQuery);
  const [operator, setOperator] = useState(defaultOperator);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange,
    transform: false,
    placement: "bottom",
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
  ]);

  useEffect(() => {
    if (isOpen) {
      setQuery(defaultQuery);
      setOperator(defaultOperator);
    }
  }, [isOpen, defaultQuery, defaultOperator]);

  return (
    <div className="w-full truncate">
      <button
        className="flex items-center font-normal h-[30px] w-full"
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        <i
          className={clsx(
            "fa-regular fa-sharp fa-list",
            "text-[0.75rem] text-gray-0 ml-0.5 mr-1.5",
          )}
        ></i>
        <div className="text-sm truncate">
          Searching ({defaultQuery.split(",").filter((e) => e).length}) values
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            transition={{ ease: "easeOut", duration: 0.2 }}
            initial={{ opacity: 0, translateY: "0.25rem" }}
            animate={{ opacity: 1, translateY: 0 }}
            exit={{ opacity: 0, translateY: "0.25rem" }}
            ref={refs.setFloating}
            style={floatingStyles}
            className={clsx(
              "absolute left-0 z-10 mt-0.5 px-4 pb-4 pt-3 w-full",
              "bg-white rounded-lg elevation-1 text-sm",
            )}
            {...getFloatingProps()}
          >
            <h4 className="text-sm font-semibold text-interstellar+2 mb-2">
              Comma separated search
            </h4>
            <p className="text-xs font-light text-gray+1 mb-3.5">
              Copy and paste a selection from a spreadsheet or .CSV file
            </p>
            <label className="text-xs text-interstellar+2 mb-1">
              SEARCH LOGIC
            </label>
            <div className="w-[9rem] mb-4">
              <Select
                value={operator}
                onChange={setOperator}
                options={savedSearchOperatorOptions()}
              />
            </div>
            <div>
              <TextAreaInput
                placeholder="Paste a comma separated list here..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                resizable
              />
            </div>
            <div className="mt-2 flex justify-end">
              <TertiaryButton
                className="mr-2 font-semibold"
                onClick={() => onCancel()}
              >
                CANCEL
              </TertiaryButton>
              <SecondaryButton
                isDisabled={query === ""}
                className="font-semibold"
                onClick={() => {
                  onApply(operator, query);
                }}
              >
                APPLY
              </SecondaryButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
